import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { TimeSeriesDataPoint } from '../../types/stats';

interface TimeSeriesTrendsProps {
  data: TimeSeriesDataPoint[];
}

export const TimeSeriesTrends = ({ data }: TimeSeriesTrendsProps) => {
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (!dateRange || (!dateRange.start && !dateRange.end)) return data;
    
    return data.filter((item) => {
      const itemDate = new Date(item.date);
      if (dateRange.start && dateRange.end) {
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999); // Include the entire end date
        return itemDate >= startDate && itemDate <= endDate;
      } else if (dateRange.start) {
        const startDate = new Date(dateRange.start);
        return itemDate >= startDate;
      } else if (dateRange.end) {
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        return itemDate <= endDate;
      }
      return true;
    });
  }, [data, dateRange]);

  const hasAcceptedWelds = useMemo(() => {
    return filteredData.some((item) => item.accepted > 0);
  }, [filteredData]);

  const dateRangeOptions = useMemo(() => {
    if (!data || data.length === 0) return { min: '', max: '' };
    const dates = data.map((item) => new Date(item.date)).sort((a, b) => a.getTime() - b.getTime());
    return {
      min: dates[0].toISOString().split('T')[0],
      max: dates[dates.length - 1].toISOString().split('T')[0],
    };
  }, [data]);

  const option = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return null;
    }

    // Format dates for display (e.g., "2025-08-25" -> "Aug 25" or "08/25")
    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      return `${month} ${day}`;
    };

    const dates = filteredData.map((item) => formatDate(item.date));
    const accepted = filteredData.map((item) => item.accepted);
    const rejected = filteredData.map((item) => item.rejected);
    const defectRates = filteredData.map((item) => item.defectRate);

    const series: echarts.SeriesOption[] = [
      {
        name: 'Accepted',
        type: 'line',
        data: accepted,
        itemStyle: { color: '#22c55e' },
        areaStyle: { color: 'rgba(34, 197, 94, 0.2)' },
        smooth: true,
      },
      {
        name: 'Rejected',
        type: 'line',
        data: rejected,
        itemStyle: { color: '#ef4444' },
        areaStyle: { color: 'rgba(239, 68, 68, 0.2)' },
        smooth: true,
      },
    ];

    const legendData = ['Accepted', 'Rejected'];
    const yAxis: echarts.YAXisComponentOption[] = [
      {
        type: 'value',
        name: 'Number of Welds',
        position: 'left',
      },
    ];

    // Only add defect rate if there are accepted welds
    if (hasAcceptedWelds) {
      series.push({
        name: 'Defect Rate %',
        type: 'line',
        yAxisIndex: 1,
        data: defectRates,
        itemStyle: { color: '#f59e0b' },
        lineStyle: { width: 3, type: 'dashed' },
        symbol: 'circle',
        symbolSize: 6,
      });
      legendData.push('Defect Rate %');
      yAxis.push({
        type: 'value',
        name: 'Defect Rate (%)',
        position: 'right',
        max: 100,
      });
    }

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: any) => {
          const index = params[0].dataIndex;
          const point = filteredData[index];
          let tooltip = `
            <strong>${point.date}</strong><br/>
            Total: ${point.total}<br/>
            Accepted: ${point.accepted}<br/>
            Rejected: ${point.rejected}
          `;
          if (hasAcceptedWelds) {
            tooltip += `<br/>Defect Rate: ${point.defectRate.toFixed(2)}%`;
          }
          return tooltip;
        },
      },
      legend: {
        data: legendData,
        top: 10,
      },
      grid: { left: 80, right: hasAcceptedWelds ? 80 : 30, top: 60, bottom: 60 },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: { 
          rotate: 45,
          formatter: (value: string) => value,
        },
        boundaryGap: false,
      },
      yAxis,
      dataZoom: [
        { type: 'slider', start: 0, end: 100 },
        { type: 'inside', start: 0, end: 100 },
      ],
      series,
    } as echarts.EChartsOption;
  }, [filteredData, hasAcceptedWelds]);

  if (!option) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No time series data available.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Time Series Trends</h3>
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Daily Performance</p>
      </div>
      <div className="flex items-center gap-4 pb-2">
        <label className="text-sm text-slate-600">Date Range:</label>
        <input
          type="date"
          value={dateRange?.start || ''}
          min={dateRangeOptions.min}
          max={dateRange?.end || dateRangeOptions.max}
          onChange={(e) => {
            const start = e.target.value;
            if (start) {
              setDateRange({
                start,
                end: dateRange?.end || dateRangeOptions.max,
              });
            } else {
              setDateRange(dateRange?.end ? { start: '', end: dateRange.end } : null);
            }
          }}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
        <span className="text-sm text-slate-500">to</span>
        <input
          type="date"
          value={dateRange?.end || ''}
          min={dateRange?.start || dateRangeOptions.min}
          max={dateRangeOptions.max}
          onChange={(e) => {
            const end = e.target.value;
            if (end) {
              setDateRange({
                start: dateRange?.start || dateRangeOptions.min,
                end,
              });
            } else {
              setDateRange(dateRange?.start ? { start: dateRange.start, end: '' } : null);
            }
          }}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
        {dateRange && (
          <button
            type="button"
            onClick={() => setDateRange(null)}
            className="text-sm text-slate-600 hover:text-slate-900 underline"
          >
            Clear
          </button>
        )}
      </div>
      <ReactECharts 
        option={option} 
        style={{ height: 450 }} 
        key="time-series-trends"
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  );
};

